import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Select, Store} from '@ngxs/store';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {OverlayPanel} from 'primeng/overlaypanel';
import {SesionState} from '@app/state/sesion/sesion.state';
import {SetCompanySelected, SetOffice, SetSaleStation} from '@app/state/sesion/sesion.actions';
import {Company} from '@app/domain/company';
import {Office} from '@app/domain/office';
import {SaleStation} from '@app/domain/sale-station';
import {ShortcutMenu} from '@app/domain/shortcut-menu';
import {LoginService} from '@app/service/login.service';
import {SaleStationService} from '@app/service/sale-station.service';

@Component({
    selector: 'app-context-switcher',
    templateUrl: './context-switcher.component.html',
    styleUrls: ['./context-switcher.component.scss']
})
export class ContextSwitcherComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    @Select(SesionState.getCompany) company$: Observable<Company>;
    @Select(SesionState.getCompanySelected) companySelected$: Observable<Company>;
    @Select(SesionState.getOffice) office$: Observable<Office>;
    @Select(SesionState.getSaleStation) saleStation$: Observable<SaleStation>;

    // valores aplicados (los que muestra el resumen del header)
    company: Company;
    companySelected: Company;
    office: Office;
    saleStation: SaleStation;

    // valores de trabajo dentro del panel; se aplican solo al confirmar
    empresaSel: Company;
    officeSel: Office;
    stationSel: SaleStation;

    attachedCompanies: Company[] = [];
    offices: Office[] = this.loginService.getPuntosVentaUsuario();
    stations: SaleStation[] = [];
    loadingStations = false;

    panelOpen = false;
    sheetVisible = false;

    // secciones extra del bottom sheet en móvil
    @Input() shortcuts: ShortcutMenu[] = [];
    @Input() printerVisible = false;
    @Input() printerConnected = false;
    @Input() whatsappVisible = false;
    @Input() whatsappConnected = false;
    @Input() whatsappRoute: string;

    @ViewChild('panel') panel: OverlayPanel;

    constructor(private loginService: LoginService,
                private saleStationService: SaleStationService,
                private store: Store) { }

    ngOnInit(): void {
        this.company$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(c => {
                this.company = c;
                this.attachedCompanies = [this.company, ...(this.loginService.getUser().empresasAdjuntas || [])];
            });
        this.companySelected$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(c => this.companySelected = c);
        this.office$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(o => this.office = o);
        this.saleStation$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(s => this.saleStation = s);
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    get canSelectCompany() {
        return this.loginService.hasPermission('SELECCIONAR_EMPRESAS_ADJUNTAS') && this.attachedCompanies.length > 1;
    }

    get isMobile() {
        return window.matchMedia('(max-width: 991px)').matches;
    }

    open(event: Event) {
        event.preventDefault();
        this.initWorkingValues();
        if (this.isMobile) {
            this.sheetVisible = true;
        } else {
            this.panel.toggle(event);
            this.panelOpen = true;
        }
    }

    close() {
        this.sheetVisible = false;
        this.panelOpen = false;
        if (this.panel) {
            this.panel.hide();
        }
    }

    onPanelHide() {
        this.panelOpen = false;
    }

    onEmpresaChange() {
        this.dispatchCompany();
        const officeAsignado = this.offices.find(o => o.rucAsignado === this.empresaSel?.ruc);
        if (officeAsignado) {
            this.officeSel = officeAsignado;
            this.dispatchOffice();
            this.loadStations(this.officeSel, null, true);
        }
    }

    onOfficeChange() {
        // espejo del acople existente: la sede con RUC asignado arrastra la empresa
        if (this.officeSel?.rucAsignado) {
            const empresa = this.attachedCompanies.find(ea => ea.ruc === this.officeSel.rucAsignado);
            if (empresa) {
                this.empresaSel = empresa;
                this.dispatchCompany();
            }
        }
        this.dispatchOffice();
        this.loadStations(this.officeSel, null, true);
    }

    onStationChange() {
        this.dispatchStation();
    }

    private dispatchCompany() {
        if (this.empresaSel && this.empresaSel.id !== this.companySelected?.id) {
            this.store.dispatch(new SetCompanySelected(this.empresaSel));
        }
    }

    private dispatchOffice() {
        if (this.officeSel && this.officeSel.id !== this.office?.id) {
            this.store.dispatch(new SetOffice({office: this.officeSel}));
        }
    }

    private dispatchStation() {
        if (this.stationSel && this.stationSel.id !== this.saleStation?.id) {
            this.store.dispatch(new SetSaleStation({saleStation: this.stationSel}));
        }
    }

    private initWorkingValues() {
        this.empresaSel = this.attachedCompanies.find(c => c?.id === this.companySelected?.id) || this.companySelected;
        this.officeSel = this.offices.find(o => o?.id === this.office?.id) || this.office;
        this.loadStations(this.officeSel, this.saleStation, false);
    }

    private loadStations(office: Office, keepCurrent: SaleStation, dispatchOnLoad: boolean) {
        this.stations = [];
        this.stationSel = null;
        if (!office) {
            return;
        }
        this.loadingStations = true;
        const params = [
            `paginacion=${false}`,
            `idPuntoVenta=${office.id}`
        ];
        this.saleStationService.findAllList(params.join('&'))
            .subscribe(data => {
                const estacionFija = this.loginService.getUser().estacionVenta;
                this.stations = estacionFija ? data.filter(ev => ev.id === estacionFija.id) : data;
                this.stationSel = (keepCurrent && this.stations.find(s => s.id === keepCurrent.id)) || this.stations[0];
                this.loadingStations = false;
                if (dispatchOnLoad) {
                    this.dispatchStation();
                }
            }, () => this.loadingStations = false);
    }

}
